/**
 * Small shared primitives = the design-system components (screenshots/00).
 * Icons: `data-icon="<lucide name>"`, rendered by the app's icon sprite (same as Sidebar.tsx).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { onTablistKey, useTabs } from "../../hooks/useTabs";

export type Tone = "neutral" | "positive" | "warning" | "negative" | "live";

/** Design: Chip / Neutral · Positive · Warning · Negative · Live */
export const Chip = ({ tone = "neutral", children, icon }: { tone?: Tone; children: ReactNode; icon?: string }) => (
  <span className={`chip chip--${tone}`}>{icon && <i data-icon={icon} aria-hidden />}{children}</span>
);

/** Design: Count Badge. Pulses once when the value changes later (never on first paint). */
export function CountBadge({ children }: { children: ReactNode }) {
  const prev = useRef(children);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (prev.current !== children) { prev.current = children; setPulse((n) => n + 1); }
  }, [children]);
  return <span className="count-badge" data-pulse={pulse || undefined} key={pulse}>{children}</span>;
}

/** Design: Section Header (title + optional count). */
export const SectionHeader = ({ title, count, children }: { title: string; count?: ReactNode; children?: ReactNode }) => (
  <div className="section-head">
    <h2>{title}</h2>
    {count !== undefined && <CountBadge>{count}</CountBadge>}
    {children && <div className="section-head__right">{children}</div>}
  </div>
);

/** Design: Tab / Tab Active, with prefetch-on-hover, scroll memory and 1–9 shortcuts. */
export function TabBar<T extends string>({ tabs, active, onChange, prefetch, label }: {
  tabs: readonly { id: T; label: string; count?: ReactNode }[];
  active: T; onChange: (t: T) => void; prefetch?: (t: T) => void; label: string;
}) {
  const ids = tabs.map((t) => t.id);
  const { tabProps } = useTabs(ids, active, onChange, prefetch);
  return (
    <div className="tabbar" role="tablist" aria-label={label} onKeyDown={onTablistKey}>
      {tabs.map((t) => (
        <button key={t.id} className="tab" {...tabProps(t.id)}>
          {t.label}{t.count !== undefined && <CountBadge>{t.count}</CountBadge>}
        </button>
      ))}
    </div>
  );
}

/** Design: View Switch (segmented, 2–3 options). */
export function ViewSwitch<T extends string>({ options, value, onChange, label }: {
  options: readonly { id: T; label: string }[]; value: T; onChange: (v: T) => void; label: string;
}) {
  return (
    <div className="view-switch" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} role="radio" aria-checked={o.id === value} onClick={() => onChange(o.id)}>{o.label}</button>
      ))}
    </div>
  );
}

/** Design: Switch / On · Off */
export const Switch = ({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) => (
  <button className="switch" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}><span /></button>
);

/** Design: Button / Primary · Secondary · Small · Danger */
export const Button = ({ kind = "secondary", icon, children, ...rest }: {
  kind?: "primary" | "secondary" | "small" | "danger"; icon?: string; children: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button className={`btn btn--${kind}`} {...rest}>{icon && <i data-icon={icon} aria-hidden />}{children}</button>
);

/** Design: Link / Arrow */
export const LinkArrow = ({ href, children, icon = "arrow-right" }: { href?: string; children: ReactNode; icon?: string }) => (
  <a className="link-arrow" href={href}>{children}<i data-icon={icon} aria-hidden /></a>
);

/** Design: Kbd */
export const Kbd = ({ children }: { children: ReactNode }) => <kbd className="kbd">{children}</kbd>;

/** Small uppercase group label with a count ("TOSS-UPS 11"). */
export const GroupLabel = ({ children, count }: { children: ReactNode; count?: ReactNode }) => (
  <div className="group-label"><span>{children}</span>{count !== undefined && <CountBadge>{count}</CountBadge>}</div>
);
